import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { useRecoilState } from 'recoil';
import { MINUTE } from 'time-constants';
import UrlPattern from 'url-pattern';
import { array, date, number, object, string } from 'yup';

import { apiConfig } from '@constants';
import { airQualityState, hasResetRefetchIntervalState } from '@store';

import { refetchIntervalByExpirationTime, refetchIntervalFallback } from '../../helpers/refetchIntervalUtils';
import { useGetUserLocation } from '../useGetUserLocation';

const YesOrNo = {
  Yes: 'Yes',
  No: 'No',
};

export const YOrN = {
  Y: 'Y',
  N: 'N',
};

export const AirQualityDataType = {
  Forecast: 'F',
  YesterdaySummary: 'Y',
  Observation: 'O',
};

const Pollutant = {
  Ozone: 'OZONE',
  PM2P5: 'PM2.5',
  PM10: 'PM10',
  CO: 'CO',
  NO2: 'NO2',
  SO2: 'SO2',
};

const AirQualityServiceType = {
  Historical: 'historical',
  DailySummary: 'summary',
};

export const AirQualityCategoryIndex = {
  Good: 1,
  Moderate: 2,
  UnhealthyToSensitiveGroups: 3,
  Unhealthy: 4,
  VeryUnhealthy: 5,
  Hazardous: 6,
};

/**
 * This schema follows U.S. Air Quality API User Document Version 1.0. (https://docs.google.com/document/d/13uwMRtW2TyqmzqlUOL-BFhdle6n0qZ-eBNq8vgnNy5g)
 */
const airQualityArgumentsSchema = object({
  lat: number().required(),
  lng: number().required(),
  service: string().oneOf(Object.values(AirQualityServiceType)).notRequired(),
  language: string().default('en-US'),
  startDate: string().when('service', {
    is: service => service && Object.values(AirQualityServiceType).includes(service),
    then: string().required(),
    otherwise: string().notRequired(),
  }),
  endDate: string().when('service', {
    is: service => service && Object.values(AirQualityServiceType).includes(service),
    then: string().required(),
    otherwise: string().notRequired(),
  }),
});

/**
 * This schema follows U.S. Air Quality API User Document Version 1.0. (https://docs.google.com/document/d/13uwMRtW2TyqmzqlUOL-BFhdle6n0qZ-eBNq8vgnNy5g)
 * However there may be a bit differences for max length of some string properties.
 */
const airQualitySchema = object({
  airquality: array().of(
    object({
      class: string().max(32).required(),
      key: string().max(100),
      area_id: string().max(5),
      area_name: string().max(60),
      state_cd: string().max(4).required(),
      country_cd: string().max(3).required(),
      latitude: number(),
      longitude: number(),
      source: string().max(100),
      rpt_dt: date().required(),
      valid_time_lap: date().required(),
      process_time_gmt: number(),
      action_day_ind: string().oneOf(Object.values(YesOrNo)),
      air_quality_cmnt: string().max(4000),
      data_type: string().oneOf(Object.values(AirQualityDataType)).required(),
      pollutants: array().of(
        object({
          primary_pollutant_ind: string().oneOf(Object.values(YOrN)),
          pollutant: string().oneOf(Object.values(Pollutant)).required(),
          pollutant_phrase: string().max(41).required(),
          air_quality_idx: number().integer().min(0).max(500).nullable(),
          air_quality_cat: string().max(40),
          air_quality_cat_idx: number().oneOf(Object.values(AirQualityCategoryIndex)),
        }),
      ),
    }),
  ),
  metadata: object({
    expire_time_gmt: number(),
    language: string(),
    latitude: number(),
    longitude: number(),
    status_code: number(),
    transaction_id: string(),
    version: string(),
  }),
});

/**
 * Gets Air Quality API URL for given arguments
 * @param {*} param0 airQualityArgumentsSchema
 * @returns Air Quality API URL
 */
const getAirQualityApiUrl = ({ lat, lng, service, language, startDate, endDate }) =>
  [
    apiConfig.TWC_API_BASE_V1 +
      new UrlPattern(apiConfig.TWC_API_AIR_QUALITY).stringify({
        lat,
        lng,
        ...(service && { service }),
      }),
    Object.entries({
      language,
      apiKey: apiConfig.TWC_API_KEY,
      ...(service && { startDate, endDate }),
    })
      .map(element => element.join('='))
      .join('&'),
  ].join('?');

/**
 * Calls Air Quality API
 */
const getAirQuality = async parameters => {
  try {
    const validateArguments = await airQualityArgumentsSchema.validate(parameters);
    const url = getAirQualityApiUrl(validateArguments);
    const { data } = await axios.get(url);

    return airQualitySchema.validate(data);
  } catch (error) {
    if (error?.response?.status === 400) {
      // The status 400 means "NDF-0001: There was no data found for your Air Quality Report query". We want to consider this as a success response
      return null;
    }
    throw error;
  }
};

export const useGetAirQuality = () => {
  const {
    coords: { lat, lng },
  } = useGetUserLocation();
  const queryClient = useQueryClient();
  const [state, setState] = useRecoilState(airQualityState);
  const [refetchInterval, setRefetchInterval] = useState(refetchIntervalFallback);

  const [hasResetRefetchInterval, setHasResetRefetchInterval] = useRecoilState(hasResetRefetchIntervalState);

  const { airQuality: airQualityRefetched } = hasResetRefetchInterval;

  // Trigger refetch when Debug date is set
  useEffect(() => {
    if (Date.prototype.debugDate && !airQualityRefetched) {
      console.log('Invalidating / Refetching Air Quality');
      // Invalidating query to trigger refetch since we don't have access to
      // metadata in the onSuccess to call the refetch fn directly
      queryClient.invalidateQueries(['getAirQuality', lat, lng]);
      setHasResetRefetchInterval({
        ...hasResetRefetchInterval,
        airQuality: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Date.prototype.debugDate]);

  const { data, error, isLoading, isFetched } = useQuery(
    ['getAirQuality', lat, lng],
    () => getAirQuality({ lat, lng }),
    {
      enabled: Boolean(lat && lng),
      refetchOnWindowFocus: false,
      refetchInterval,
      retry: false,
      onSettled: _data => {
        if (_data?.metadata?.expire_time_gmt) {
          refetchIntervalByExpirationTime({
            expirationTimeUtc: _data.metadata.expire_time_gmt,
            setRefetchInterval,
          });
        } else {
          setRefetchInterval(2 * MINUTE);
        }
      },
    },
  );

  useEffect(() => {
    setState({ data, error, isFetched, isLoading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading, isFetched]);

  return state;
};
